import {useDropdown} from '@/hooks/Dropdown-hook';
import {useAppSelector} from '@/store';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPES_ENUM,
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {ALLOWED_ACTIVITY_SEGMENT_MARKETS} from '@/utils/constants/globalConstants';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getSegmentsList} from '@/services/rewards';
import {formatUTCTimestamp} from '@/utils/commons';
import {logger} from '@/helpers/logger';

const useActivitySetup = () => {
  const dispatch = useDispatch();
  const activityTypes = useSelector((state) => state.activity.activityTypes);
  const activityFormData = useAppSelector((state) => state.activity.formData); // for Edit
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  // Segments related state - fetch from API
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);

  const [formData, setFormData] = useState({
    allCustomers: true,
    segmentExclude: false,
    selectedSegments: [],
    excludedSegments: []
  });

  const [isInitialized, setIsInitialized] = useState(false);

  const {dropdownList, isLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.ActivitiesType
  });

  const activityTypeForSegments =
    formData?.activityType || activityFormData?.activityType || '';

  const showSegments = useMemo(() => {
    return (
      ALLOWED_ACTIVITY_SEGMENT_MARKETS.includes(countryCode) ||
      activityTypeForSegments === ACTIVITY_TYPE.SURVEY ||
      isVideoActivityType(activityTypeForSegments) ||
      isFreeKickActivityType(activityTypeForSegments) ||
      isWheelOfFortuneActivityType(activityTypeForSegments) ||
      isBackgammonActivityType(activityTypeForSegments)
    );
  }, [countryCode, activityTypeForSegments]);

  // Update formData when activityFormData changes (for edit mode)
  useEffect(() => {
    if (
      activityFormData &&
      Object.keys(activityFormData).length > 0 &&
      !isInitialized
    ) {
      const selected =
        activityFormData?.selectedSegments ?? activityFormData?.segments;
      const hasSegments = Array.isArray(selected) && selected.length > 0;
      const hasExcludedSegments =
        activityFormData?.excludedSegments?.length > 0;

      const newFormData = {
        ...activityFormData,
        allCustomers:
          activityFormData?.allCustomers ??
          (!hasSegments && !hasExcludedSegments),
        segmentExclude:
          activityFormData?.segmentExclude ??
          activityFormData?.excludeSegments ??
          false,
        selectedSegments: Array.isArray(selected) ? selected : [],
        excludedSegments: activityFormData?.excludedSegments ?? []
      };

      if (
        (isVideoActivityType(newFormData.activityType) ||
          newFormData.activityType === ACTIVITY_TYPE.SURVEY) &&
        newFormData.allTimeLimit != null &&
        newFormData.allTimeLimit !== ''
      ) {
        newFormData.allTimeLimit = String(newFormData.allTimeLimit);
      }
      delete newFormData.allTimeLoginLimitValid;
      delete newFormData.allTimeLimitValid;
      setFormData(newFormData);
      setIsInitialized(true);
    }
  }, [activityFormData, isInitialized]);

  useEffect(() => {
    if (
      !isInitialized ||
      !Array.isArray(dropdownList) ||
      dropdownList.length === 0
    ) {
      return;
    }
    const t = formData?.activityType;
    if (!t) {
      return;
    }

    const watchVideoOption = dropdownList.find(
      (o) => o?.value === ACTIVITY_TYPES_ENUM.WATCH_VIDEO
    );
    const watchVideoLabel =
      watchVideoOption?.displayText ?? watchVideoOption?.label;
    if (
      watchVideoLabel &&
      (t === ACTIVITY_TYPES_ENUM.WATCH_VIDEO ||
        t === ACTIVITY_TYPE.WATCH_VIDEO) &&
      t !== watchVideoLabel
    ) {
      setFormData((prev) => ({...prev, activityType: watchVideoLabel}));
    }
  }, [isInitialized, dropdownList, formData?.activityType]);

  const activityDropdownTypes = useMemo(() => {
    if (dropdownList?.length <= 0) return {};
    const updatedDropdownData = structuredClone(activityTypes);
    updatedDropdownData.properties.activityType.options = dropdownList;
    return updatedDropdownData;
  }, [activityTypes, dropdownList]);

  const fetchSegments = useCallback(async () => {
    setSegmentsLoading(true);
    try {
      const response = await getSegmentsList('', []);
      if (response?.success && Array.isArray(response?.data)) {
        setSegments(response.data);
      }
    } catch (error) {
      logger.error('Failed to fetch segments:', error);
    } finally {
      setSegmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showSegments) return;
    fetchSegments();
  }, [fetchSegments, showSegments]);

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      const activityType = data?.activityType ?? prev?.activityType;
      const shouldClearEndDate =
        data?.hasEndDate === false &&
        (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
          activityType === ACTIVITY_TYPE.PAY_BY_POINTS ||
          activityType === ACTIVITY_TYPE.EASY_ORDER);
      if (shouldClearEndDate) {
        const nextData = {...prev, ...data};
        delete nextData.endDate;
        delete nextData.endDateValid;
        return nextData;
      }
      return {...prev, ...data};
    });
  }, []);
  useEffect(() => {
    if (isInitialized) {
      dispatch(setActivityFormData(formData));
    }
  }, [formData, dispatch, isInitialized]);

  const handleResetFormData = useCallback(
    (data) => {
      // Only proceed if activityType is present in the data
      if (!data?.activityType) {
        return;
      }

      const activityType = data?.activityType;

      const defaultFormData = {
        activityType: activityType,
        hasEndDate: false,
        uuid: data?.uuid || '',
        id: data?.id || '', // Preserve the id
        allCustomers: true,
        segmentExclude: false,
        selectedSegments: [],
        excludedSegments: [],
        allTimeLoginLimit: '' // default for non-Video activity types
      };
      if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
        defaultFormData.activityName = `PAY_BY_POINTS_${formatUTCTimestamp()}`;
        // Leave pointsToRedeem and localCurrencyValue undefined so fields don't show error on mount
      }
      if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
        defaultFormData.activityName = `CURRENCY_TO_POINTS_${formatUTCTimestamp()}`;
        // Leave localCurrency and pointsEarned undefined so fields don't show error on mount
      }
      if (activityType === ACTIVITY_TYPE.SURVEY) {
        // Leave activityName and pointsEarned unset (undefined) so fields match Welcome /
        // Pay-by-Points behavior — explicit '' triggers TextInputField mount validation and shows errors immediately.
        defaultFormData.startDate = null;
        defaultFormData.endDate = null;
        defaultFormData.allTimeLimit = '';
        defaultFormData.displayOrder = 0;
      }
      if (isVideoActivityType(activityType)) {
        // Video uses allTimeLimit (not allTimeLoginLimit)
        delete defaultFormData.allTimeLoginLimit;
        defaultFormData.startDate = null;
        defaultFormData.endDate = null;
        defaultFormData.allTimeLimit = '';
        defaultFormData.displayOrder = 0;
      }
      if (isFreeKickActivityType(activityType)) {
        defaultFormData.startDate = null;
        defaultFormData.endDate = null;
        defaultFormData.dailyDisplayStartTime = '';
        defaultFormData.dailyDisplayEndTime = '';
        defaultFormData.daysOfWeekUnavailable = [];
        defaultFormData.dailyPlayLimit = 1;
      }
      if (isWheelOfFortuneActivityType(activityType)) {
        defaultFormData.startDate = null;
        defaultFormData.endDate = null;
        defaultFormData.dailyDisplayStartTime = '';
        defaultFormData.dailyDisplayEndTime = '';
        defaultFormData.daysOfWeekUnavailable = [];
        defaultFormData.dailyPlayLimit = 1;
      }
      if (isBackgammonActivityType(activityType)) {
        defaultFormData.startDate = null;
        defaultFormData.endDate = null;
        defaultFormData.dailyDisplayStartTime = '';
        defaultFormData.dailyDisplayEndTime = '';
        defaultFormData.daysOfWeekUnavailable = [];
        defaultFormData.dailyPlayLimit = 1;
      }
      if (activityType === ACTIVITY_TYPE.EASY_ORDER) {
        defaultFormData.hasEndDate = false;
        defaultFormData.photoOrderModalDisplayLimit = 0;
        defaultFormData.startDate = null;
        defaultFormData.activityName = `EASY_ORDER_${formatUTCTimestamp()}`;
      }
      // Preserve optional rich-content fields from input if provided
      if (data?.translations) {
        defaultFormData.translations = data.translations;
      }
      if (data?.mediaUrls) {
        defaultFormData.mediaUrls = data.mediaUrls;
      }
      setFormData(defaultFormData);
      setIsInitialized(false); // Reset initialization flag
      dispatch(resetActivityData(defaultFormData));
    },
    [dispatch]
  );

  // Segments handlers
  const handleAllCustomersChange = useCallback((value) => {
    setFormData((prev) => {
      const updatedData = {...prev, allCustomers: value};
      if (value) {
        updatedData.selectedSegments = [];
      }
      return updatedData;
    });
  }, []);

  const handleExcludeSegmentsChange = useCallback((value) => {
    setFormData((prev) => {
      const updatedData = {...prev, segmentExclude: value};
      if (!value) {
        updatedData.excludedSegments = [];
      }
      return updatedData;
    });
  }, []);

  const handleSegmentsChange = useCallback((segmentsData) => {
    setFormData((prev) => ({...prev, selectedSegments: segmentsData}));
  }, []);

  const handleExcludedSegmentsChange = useCallback((excludedSegmentsData) => {
    setFormData((prev) => ({...prev, excludedSegments: excludedSegmentsData}));
  }, []);

  return {
    formData,
    activityTypes,
    handleFormChange,
    dropdownList,
    activityDropdownTypes,
    handleResetFormData,
    isLoading,
    showSegments,
    segments,
    segmentsLoading,
    handleAllCustomersChange,
    handleExcludeSegmentsChange,
    handleSegmentsChange,
    handleExcludedSegmentsChange
  };
};

export default useActivitySetup;
